// Fenster zum Überarbeiten eines Monatsberichts per Anweisung.
//
// Nico schreibt in eigenen Worten, was ihm nicht gefällt — die Formulierung
// wird neu geschrieben, die Zahlen bleiben unverändert. Das steht auch im
// Fenster, damit klar ist, wo die Grenze liegt: Deutung ja, Fakten nein.

import { useEffect, useRef, useState } from 'react';
import { Loader2, Pencil, X } from 'lucide-react';

const BEISPIELE = [
  'Der Abschnitt zu den Haustüren klingt zu negativ — sachlicher formulieren und den Blick nach vorn richten.',
  'Die Einleitung ist zu lang. Kürze sie auf zwei Sätze.',
  'Erkläre bei den Anzeigen deutlicher, warum wir das Budget verschoben haben.',
  'Nimm die Fachbegriffe raus und formuliere durchgehend einfacher.',
];

export default function BerichtBearbeiten({
  kunde,
  monat,
  laeuft,
  onAbbrechen,
  onSenden,
}: {
  kunde: string;
  monat: string;
  laeuft: boolean;
  onAbbrechen: () => void;
  onSenden: (anweisung: string) => void;
}) {
  const [text, setText] = useState('');
  const feld = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    feld.current?.focus();
    const zu = (e: KeyboardEvent) => { if (e.key === 'Escape' && !laeuft) onAbbrechen(); };
    window.addEventListener('keydown', zu);
    return () => window.removeEventListener('keydown', zu);
  }, [laeuft, onAbbrechen]);

  const absenden = () => {
    const a = text.trim();
    if (!a || laeuft) return;
    onSenden(a);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 p-4"
      onClick={() => !laeuft && onAbbrechen()}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <Pencil className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900">Bericht überarbeiten</h2>
            <p className="truncate text-xs text-gray-500">{kunde} · {monat}</p>
          </div>
          <button
            onClick={onAbbrechen}
            disabled={laeuft}
            aria-label="Schließen"
            className="ml-auto rounded-lg p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div>
            <label htmlFor="anweisung" className="mb-1.5 block text-xs font-medium text-gray-700">
              Was soll anders werden?
            </label>
            <textarea
              id="anweisung"
              ref={feld}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) absenden(); }}
              rows={5}
              disabled={laeuft}
              placeholder="Schreib in eigenen Worten, was dir nicht gefällt — eine Textpassage, ein Tonfall, die Deutung einer Zahl …"
              className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-50"
            />
            <p className="mt-1.5 text-[11px] text-gray-400">⌘/Strg + Enter sendet ab.</p>
          </div>

          <div>
            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">Beispiele</p>
            <div className="flex flex-col gap-1.5">
              {BEISPIELE.map((bsp) => (
                <button
                  key={bsp}
                  onClick={() => setText(bsp)}
                  disabled={laeuft}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-left text-xs text-gray-600 hover:border-blue-300 hover:bg-blue-50/50 disabled:opacity-50"
                >
                  {bsp}
                </button>
              ))}
            </div>
          </div>

          <p className="rounded-lg bg-gray-50 px-3 py-2 text-[11px] leading-relaxed text-gray-500">
            <strong className="font-medium text-gray-700">Die Zahlen bleiben unangetastet.</strong> Überarbeitet wird
            nur der Text — Besucher, Anfragen, Kosten und Tabellen im PDF ändern sich nicht. Eine andere{' '}
            <em>Deutung</em> einer Zahl kannst du verlangen, eine andere Zahl nicht.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/60 px-5 py-3">
          <button
            onClick={onAbbrechen}
            disabled={laeuft}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            onClick={absenden}
            disabled={laeuft || !text.trim()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {laeuft ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pencil className="h-3.5 w-3.5" />}
            {laeuft ? 'Wird überarbeitet…' : 'Überarbeiten'}
          </button>
        </div>
      </div>
    </div>
  );
}
